import React, { useState, useEffect, useRef } from "react";
import { ChevronDown, ArrowRight, Volume2, ShieldCheck, Beaker, BarChart3, ExternalLink } from "lucide-react";
import MaroonNavbar from "@/components/new/MaroonNavbar";
import MaroonFooter from "@/components/new/MaroonFooter";
import WhatsAppButton from "@/components/new/WhatsAppButton";

const C = { maroon: "#621B21", maroonDark: "#5C0F1C", yellow: "#F5C800", beige: "#F5EDD6", white: "#FFFFFF" };
const SCIENCE_VIDEO = "https://customer-assets.emergentagent.com/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/podj385u_science%20explaination%20video.mp4";
const BOWL_IMG = "https://customer-assets.emergentagent.com/job_2e8a6a93-30a9-4e91-a7a5-a9394acf49bd/artifacts/dv899zqk_bowl%20image.png";

const useReveal = () => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => { const el = ref.current; if (!el) return; const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold: 0.2 }); o.observe(el); return () => o.disconnect(); }, []);
  return [ref, vis];
};

/* ─── Hero ─── */
const HeroSection = () => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const toggleMute = () => { if (videoRef.current) { videoRef.current.muted = !muted; setMuted(!muted); } };
  return (
    <section className="pt-32 md:pt-40 pb-16 md:pb-24" style={{ background: C.beige }} data-testid="science-hero">
      <div className="max-w-[1100px] mx-auto px-6 md:px-[52px]">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-10 lg:gap-16 items-center">
          <div className="flex justify-center">
            <div className="relative rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl w-full max-w-[320px]" style={{ aspectRatio: "9/16" }}>
              <video ref={videoRef} src={SCIENCE_VIDEO} autoPlay muted loop playsInline className="w-full h-full object-cover" data-testid="science-video" />
              <button onClick={toggleMute} className="absolute bottom-4 right-4 w-10 h-10 rounded-full flex items-center justify-center" style={{ background: C.yellow }} data-testid="science-unmute">
                <Volume2 size={18} style={{ color: C.maroon }} />
              </button>
            </div>
          </div>
          <div className="text-center lg:text-left">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black leading-tight mb-8" style={{ color: C.maroon, fontFamily: "Fraunces, serif" }} data-testid="science-headline">
              No farm.<br />No slaughter.<br /><span style={{ color: C.maroonDark }}>No compromise.</span>
            </h1>
            <p className="text-sm md:text-base leading-relaxed" style={{ color: `${C.maroon}99`, fontFamily: "Manrope, sans-serif" }}>
              This is not imitation meat.<br />And it is not a trend.<br />It is the future of feeding — built from science, responsibility, and compassion.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─── Nutrition Hearts ─── */
const NutritionSection = () => {
  const nutrients = [
    { name: "Protein", value: "8% Min", color: C.yellow },
    { name: "Fat", value: "1% Min", color: "#5C6E2E" },
    { name: "Fiber", value: "1.5% Min", color: "transparent", border: true },
    { name: "Carbs", value: "10% Max", color: C.yellow },
    { name: "Energy", value: "80kcal", color: "#5C6E2E" },
    { name: "Moisture", value: "80% Max", color: "transparent", border: true },
  ];
  const [ref, vis] = useReveal();
  return (
    <section ref={ref} className="py-16 md:py-24" style={{ background: C.maroon }} data-testid="nutrition-section">
      <div className="max-w-[900px] mx-auto px-6">
        <div className="text-center mb-4"><span className="text-xs font-bold tracking-[0.2em] uppercase" style={{ color: C.yellow }}>NUTRITIONAL PROFILE</span></div>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-center mb-3" style={{ color: C.white, fontFamily: "Fraunces, serif" }}>Thoughtfully Formulated Nutrition</h2>
        <p className="text-center text-sm md:text-base mb-12" style={{ color: `${C.beige}CC` }}>Every ETHIA pouch is scientifically formulated for your dog's health.</p>
        <div className={`grid grid-cols-3 md:grid-cols-6 gap-4 md:gap-6 transition-all duration-700 ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {nutrients.map((n, i) => <NutrientHeart key={i} nutrient={n} />)}
        </div>
      </div>
    </section>
  );
};

const NutrientHeart = ({ nutrient }) => {
  const [flipped, setFlipped] = useState(false);
  return (
    <div className="flex flex-col items-center cursor-pointer" onClick={() => setFlipped(!flipped)} data-testid={`nutrient-${nutrient.name.toLowerCase()}`}>
      <div className="relative w-20 h-20 md:w-24 md:h-24 transition-transform duration-400" style={{ transform: flipped ? "rotateY(180deg)" : "none", transformStyle: "preserve-3d" }}>
        <div className="absolute inset-0 flex flex-col items-center justify-center rounded-full" style={{ background: nutrient.color === "transparent" ? "transparent" : nutrient.color, border: nutrient.border ? `2px solid ${C.white}` : "none", backfaceVisibility: "hidden" }}>
          <span className="text-lg md:text-xl font-black" style={{ color: nutrient.color === C.yellow ? C.maroon : C.white, fontFamily: "Fraunces, serif" }}>{nutrient.value.split(" ")[0]}</span>
        </div>
        <div className="absolute inset-0 flex items-center justify-center rounded-full p-2" style={{ background: C.yellow, transform: "rotateY(180deg)", backfaceVisibility: "hidden" }}>
          <span className="text-[10px] text-center font-bold" style={{ color: C.maroon }}>{nutrient.name}: {nutrient.value}</span>
        </div>
      </div>
      <span className="text-xs mt-2 font-medium" style={{ color: `${C.beige}CC` }}>{nutrient.name}</span>
    </div>
  );
};

/* ─── Ingredients ─── */
const INGREDIENT_MAP = [
  { name: "Cultured Chicken Cells", benefit: "Protein" },
  { name: "Pea Protein", benefit: "Plant Protein" },
  { name: "Shiitake Mushroom Extract", benefit: "Umami Flavour" },
  { name: "Chickpea Flour", benefit: "Fiber Source" },
  { name: "Pumpkin Powder", benefit: "Pumpkin Fiber" },
  { name: "Potato Starch", benefit: "Carb Source" },
  { name: "Yeast Extract", benefit: "Umami Flavour" },
  { name: "Sodium Alginate", benefit: "Binding Agent" },
  { name: "Vitamins and Minerals", benefit: "Essential Nutrients" },
];

const IngredientsSection = () => {
  const [activeIdx, setActiveIdx] = useState(null);
  const [ref, vis] = useReveal();
  const toggle = (i) => setActiveIdx(activeIdx === i ? null : i);
  return (
    <section ref={ref} className="py-14 md:py-20" style={{ background: C.beige }} data-testid="ingredients-section">
      <div className="max-w-[1100px] mx-auto px-5 md:px-[52px]">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-2" style={{ color: C.maroon, fontFamily: "Fraunces, serif" }}>What Goes In The Bowl</h2>
        <p className="text-center text-sm md:text-base mb-10" style={{ color: `${C.maroon}99` }}>Every ingredient has a name you know and a reason to be there.</p>
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center transition-all duration-700 ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="space-y-2">
            {INGREDIENT_MAP.map((ing, i) => (
              <div key={i} className="relative flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-white/60 transition-colors" onMouseEnter={() => setActiveIdx(i)} onMouseLeave={() => setActiveIdx(null)} onClick={() => toggle(i)}>
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: C.yellow }} />
                <span className="text-sm font-bold" style={{ color: C.maroon, fontFamily: "Manrope, sans-serif" }}>{ing.name}</span>
                {activeIdx === i && (
                  <span className="ml-2 px-3 py-1 rounded-full text-[10px] font-bold animate-[fadeIn_0.2s_ease-out]" style={{ background: C.yellow, color: C.maroon }}>{ing.benefit}</span>
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-center">
            <img src={BOWL_IMG} alt="ETHIA Bowl" className="w-full max-w-sm rounded-2xl" style={{ boxShadow: "0 8px 32px rgba(98,27,33,0.1)" }} />
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─── Research Section (NEW) ─── */
const STUDIES = [
  {
    eyebrow: "Survey Analysis · Scientific Reports · 2022",
    title: "Investigating the Market for Cultivated Meat as Pet Food: A Survey Analysis",
    authors: "Olynk Widmar N, Bir C, Wolf C, et al. · Scientific Reports",
    summary: "This study analyzes consumer behavior and market acceptance regarding cultivated meat as a pet food ingredient. Based on a survey of 729 respondents, more than half of pet owners expressed a willingness to feed cultivated meat to their companion animals. Acceptance was even higher among vegan and vegetarian respondents, exceeding 55%. The findings suggest that consumers looking to reduce the environmental impact of pet diets view cultivated meat as a practical and sustainable alternative to conventional livestock-based pet food ingredients.",
    link: "https://pmc.ncbi.nlm.nih.gov/articles/PMC9803157/",
  },
  {
    eyebrow: "Scientific Review · Frontiers in Animal Science · 2025",
    title: "Alternative Proteins: An Innovative Approach to Dog Food Production",
    authors: "Carrillo-Huerta YD, Gutiérrez-Chávez AJ, Pérez-Zavala ML, Casados-Vázquez LE and Barboza-Corona JE · Front. Anim. Sci. 6:1701677",
    summary: "This scientific review evaluates the environmental footprint of traditional livestock pet food and analyzes the potential of alternative proteins — specifically cellular agriculture (cultured meat and precision fermentation), insect meal, fungi, and plant-based ingredients — for canine diets. It highlights that cultivated meat and precision-fermented proteins can be bioengineered to match the exact essential amino acid requirements for dogs (such as taurine, lysine, and methionine) while eliminating the greenhouse gas emissions, land use, and antibiotic risks associated with conventional meat slurries.",
    link: "https://www.frontiersin.org/journals/animal-science/articles/10.3389/fanim.2025.1701677",
  },
  {
    eyebrow: "Review · Frontiers in Nutrition · 2020",
    title: "Sensorial and Nutritional Aspects of Cultured Meat in Comparison to Traditional Meat",
    authors: "Fraeye I, Kratka M, Vandenburgh H, Thorrez L. · Front Nutr. 2020;7:35",
    summary: "Fraeye et al. demonstrate that cultivated meat can match or exceed the nutritional value of conventional meat through precise bioprocessing. By controlling the growth medium, producers can enrich fat cells with heart-healthy omega-3 and omega-6 fatty acids, lower saturated fats, and deliver essential micronutrients like heme iron, zinc, and vitamin B12 — offering a complete protein source tailored for optimized cardiovascular health.",
    link: "https://pmc.ncbi.nlm.nih.gov/articles/PMC7105824/",
  },
];

const ResearchSection = () => {
  const [ref, vis] = useReveal();
  return (
    <section ref={ref} className="py-16 md:py-24" style={{ background: C.beige }} data-testid="research-section">
      <div className="max-w-[900px] mx-auto px-5 md:px-[52px]">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-2" style={{ color: C.maroon, fontFamily: "Fraunces, serif" }}>Research</h2>
        <p className="text-center text-sm md:text-base mb-10" style={{ color: `${C.maroon}99` }}>Independent, peer-reviewed studies on cultivated meat.</p>
        <div className={`flex flex-col gap-16 transition-all duration-700 ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {STUDIES.map((s, i) => (
            <div key={i} className="rounded-xl p-6 md:p-8 hover:-translate-y-0.5 transition-all duration-300" style={{ background: C.white, boxShadow: "0 2px 12px rgba(98,27,33,0.06)" }} data-testid={`study-card-${i}`}>
              <span className="text-[10px] font-bold tracking-[0.12em] uppercase block mb-3" style={{ color: `${C.maroon}80` }}>{s.eyebrow}</span>
              <h3 className="text-base md:text-lg font-bold mb-2 leading-snug" style={{ color: C.maroon, fontFamily: "Fraunces, serif" }}>{s.title}</h3>
              <p className="text-xs italic mb-3" style={{ color: `${C.maroon}99` }}>{s.authors}</p>
              <p className="text-sm leading-relaxed mb-4" style={{ color: `${C.maroon}CC` }}>{s.summary}</p>
              <a href={s.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs font-bold transition-colors hover:underline" style={{ color: C.maroon }} data-testid={`study-link-${i}`}>
                Read the full study <ExternalLink size={12} />
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── Why Different (moved from Products page, restyled dark) ─── */
const WhyDifferent = () => {
  const [ref, vis] = useReveal();
  const cards = [
    { icon: <ShieldCheck size={28} />, title: "No Slaughter", desc: "Real chicken protein grown from cells. No animals raised or harmed at any stage." },
    { icon: <Beaker size={28} />, title: "No Contamination Risk", desc: "Produced in a sterile bioreactor. Zero antibiotics. Zero hormones. Zero pathogens." },
    { icon: <BarChart3 size={28} />, title: "Consistent Nutrition", desc: "Every batch identical. Thoughtfully formulated with your dog's nutritional needs in mind." },
  ];
  return (
    <section ref={ref} className="py-16 md:py-24" style={{ background: C.maroonDark }} data-testid="why-different-section">
      <div className="max-w-[1100px] mx-auto px-6 md:px-[52px]">
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start transition-all duration-700 ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 leading-tight" style={{ color: C.white, fontFamily: "Fraunces, serif" }}>Premium Nutrition.<br />Next-gen Feeding.<br /><span style={{ color: C.yellow }}>Ethical Upgrade.</span></h2>
            <p className="text-sm md:text-base leading-relaxed" style={{ color: `${C.beige}CC` }}>
              Real chicken — grown safely in a controlled environment. Same protein your dog loves. Without the slaughter, the contamination, or the compromise.
            </p>
          </div>
          <div className="space-y-4">
            {cards.map((card, i) => (
              <div key={i} className="rounded-xl p-5 flex gap-4 items-start hover:-translate-y-0.5 transition-all duration-300" style={{ background: "rgba(255,255,255,0.06)" }} data-testid={`why-card-${i}`}>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${C.yellow}20`, color: C.yellow }}>{card.icon}</div>
                <div>
                  <h4 className="font-bold text-sm mb-1" style={{ color: C.beige, fontFamily: "Fraunces, serif" }}>{card.title}</h4>
                  <p className="text-xs leading-relaxed" style={{ color: `${C.beige}99` }}>{card.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─── Taste the Future CTA ─── */
const TasteFutureCTA = () => (
  <section className="py-16 md:py-24" style={{ background: C.maroonDark }} data-testid="taste-future-cta">
    <div className="max-w-[800px] mx-auto px-6 text-center">
      <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6" style={{ color: C.white, fontFamily: "Fraunces, serif" }}>
        Taste the Future. <span style={{ color: C.yellow }}>It's on us.</span>
      </h2>
      <a href="/early-access" className="inline-block font-bold px-10 py-4 rounded-full transition-all hover:scale-105 text-sm md:text-base" style={{ background: C.yellow, color: C.maroon }} data-testid="taste-cta-btn">
        Claim Your Founders Discount <ArrowRight className="inline ml-1" size={18} />
      </a>
      <p className="text-sm mt-4" style={{ color: `${C.beige}CC` }}>Upto 30% off* for our first 1,000 founding members.</p>
    </div>
  </section>
);

/* ─── FAQ ─── */
const FAQS = [
  { q: "What is cultivated pet food?", a: "Cultivated pet food is made using cultivated chicken cells grown in a controlled environment, without raising or slaughtering animals. ETHIA uses cultivated chicken cells blended with plant and algal proteins in a sterile bioreactor to create wet food protein for your adorable pet." },
  { q: "How are the initial chicken cells sourced?", a: "Our chicken cells are sourced from a cell bank. This is a one-time sourcing process. Once we obtain the cells, we preserve and maintain our own cell bank internally, which allows us to culture and reproduce cells repeatedly without ever going back to an animal." },
  { q: "Is ETHIA safe for my dog?", a: "Absolutely! ETHIA is made with real chicken cells and is vet-approved. Our formula is lab-tested, antibiotic-free, and contains no harmful additives or byproducts." },
  { q: "How does cultivated meat compare nutritionally?", a: "ETHIA is formulated to provide dogs with key nutrients they need as part of a balanced diet. It uses cultivated chicken protein and is designed with canine nutritional requirements in mind." },
  { q: "Why is ETHIA better than regular pet food?", a: "Most conventional pet food contains meat derivatives, unknown sourcing, artificial preservatives, and fillers. ETHIA has a clean, readable ingredient list. No slaughter. No mystery. No compromise." },
];

const FAQSection = () => {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <section className="py-16 md:py-24" style={{ background: C.maroonDark }} data-testid="science-faq">
      <div className="max-w-[680px] mx-auto px-6">
        <div className="text-center mb-4"><span className="text-xs font-bold tracking-[0.2em] uppercase" style={{ color: C.yellow }}>FAQS</span></div>
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-center mb-12" style={{ color: C.white, fontFamily: "Fraunces, serif" }}>Your Questions Answered</h2>
        {FAQS.map((faq, i) => (
          <div key={i} style={{ borderBottom: `1px solid ${C.maroon}` }}>
            <button onClick={() => setOpenIdx(openIdx === i ? null : i)} className="w-full py-5 flex items-center justify-between text-left gap-4">
              <span className="font-medium text-sm md:text-base" style={{ color: C.beige }}>{faq.q}</span>
              <ChevronDown size={20} className={`flex-shrink-0 transition-transform duration-300 ${openIdx === i ? "rotate-180" : ""}`} style={{ color: C.yellow }} />
            </button>
            <div className={`overflow-hidden transition-all duration-300 ${openIdx === i ? "max-h-[500px] pb-5" : "max-h-0"}`}>
              <p className="text-xs md:text-sm leading-relaxed pr-8" style={{ color: `${C.beige}BB` }}>{faq.a}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

/* ─── Page ─── */
const SciencePage = () => {
  useEffect(() => { document.title = "ETHIA | Our Science"; }, []);
  return (
    <div className="min-h-screen" style={{ fontFamily: "Manrope, sans-serif" }} data-testid="science-page">
      <MaroonNavbar />
      <HeroSection />
      <NutritionSection />
      <IngredientsSection />
      <ResearchSection />
      <WhyDifferent />
      <TasteFutureCTA />
      <FAQSection />
      <MaroonFooter />
      <WhatsAppButton />
    </div>
  );
};

export default SciencePage;
